import { IndirectCostCategoryTypeApiModel } from '@/core/models/api/indirect-cost-category-type-api-model.model';
import { IndirectAutoLinkApplyItemApiModel, IndirectAutoLinkPreviewApiModel } from '@/core/models/api/indirect-auto-link-api.model';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { Observable } from 'rxjs';
import { BudgetControlItemApiModel } from '@/core/models/api/budget-control-item-api.model';
import { ProjectStructureApiModel } from '@/core/models/api/project-structure.model';
import { ProgressDashboardApiModel, ProgressDashboardItemApiModel } from '../models/api/progress-dashboard-api-model';
import { BciWorklogApiModel } from '../models/api/bci-worklog-api-model.model';
import { BciWarehouseApiModel } from '../models/api/bci-warehouse-api-model.model';
import { BciConsumedResourcesApiModel } from '../models/api/bci-consumed-resources-api-model.model';
import {
  BudgetControlItemCatalogApiModel, WarehouseImputationRequest, WarehouseImputationResult,
  WorklogTransferRequest, WorklogTransferResult,
} from '../models/api/budget-control-item-catalog-api.model';

@Injectable({
  providedIn: 'root'
})
export class BudgetControlItemService {
  apiEndPoint = environment.apiEndPoint;

  constructor(private httpClient: HttpClient) { }

  /**
   * Fecha local sin huso (`yyyy-MM-ddTHH:mm:ss`). `toISOString()` pasa a UTC y en UTC-5 el corte
   * "31 ago 23:59:59" viaja como 1-sep 04:59:59Z: el backend lo lee como día 1 y el mes se traga
   * el primer día del siguiente.
   */
  private toLocalIso(d: Date): string {
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
  }

  getAll(): Observable<BudgetControlItemApiModel[]> {
    return this.httpClient.get<BudgetControlItemApiModel[]>(`${this.apiEndPoint}/BudgetControlItem`);
  }

  getById(id: number): Observable<BudgetControlItemApiModel> {
    return this.httpClient.get<BudgetControlItemApiModel>(`${this.apiEndPoint}/BudgetControlItem/${id}`);
  }

  add(item: BudgetControlItemApiModel): Observable<BudgetControlItemApiModel> {
    return this.httpClient.post<BudgetControlItemApiModel>(`${this.apiEndPoint}/BudgetControlItem`, item);
  }

  update(item: BudgetControlItemApiModel): Observable<BudgetControlItemApiModel> {
    return this.httpClient.put<BudgetControlItemApiModel>(
      `${this.apiEndPoint}/BudgetControlItem/${item.budgetControlItemId}`, 
      item
    );
  }

  delete(id: number): Observable<void> {
    return this.httpClient.delete<void>(`${this.apiEndPoint}/BudgetControlItem/${id}`);
  }
  
  getByProjectId(projectId: number): Observable<ProjectStructureApiModel[]> {
    return this.httpClient.get<ProjectStructureApiModel[]>(
      `${this.apiEndPoint}/BudgetControlItem/ByProject/${projectId}`
    );
  }

  /**
   * Capítulos de costos indirectos del presupuesto vigente, para ligarlos a mano en Partidas de
   * Control. Misma fuente que usa el autocompletado, así la UI no puede ofrecer otra cosa.
   */
  getIndirectCostCategoryTypes(projectId: number): Observable<IndirectCostCategoryTypeApiModel[]> {
    return this.httpClient.get<IndirectCostCategoryTypeApiModel[]>(
      `${this.apiEndPoint}/BudgetControlItem/IndirectCostCategoryTypes/${projectId}`);
  }

  /** Simulación de "Ligar automáticamente" en Costos Indirectos (no escribe nada). */
  previewIndirectAutoLink(projectId: number): Observable<IndirectAutoLinkPreviewApiModel> {
    return this.httpClient.get<IndirectAutoLinkPreviewApiModel>(
      `${this.apiEndPoint}/BudgetControlItem/IndirectAutoLink/Preview/${projectId}`);
  }

  applyIndirectAutoLink(projectId: number, items: IndirectAutoLinkApplyItemApiModel[]): Observable<ProjectStructureApiModel[]> {
    return this.httpClient.post<ProjectStructureApiModel[]>(
      `${this.apiEndPoint}/BudgetControlItem/IndirectAutoLink/Apply/${projectId}`, items);
  }

  assignMissingRubros(projectId: number): Observable<ProjectStructureApiModel[]> {
    return this.httpClient.post<ProjectStructureApiModel[]>(
      `${this.apiEndPoint}/BudgetControlItem/AssignMissingRubros/${projectId}`, 
      {}
    );
  }

  saveAll(projectId: number, structure: ProjectStructureApiModel[]): Observable<ProjectStructureApiModel[]> {
    return this.httpClient.post<ProjectStructureApiModel[]>(
      `${this.apiEndPoint}/BudgetControlItem/SaveAll/${projectId}`, 
      structure
    );
  }

  createAutomatically(projectId: number): Observable<ProjectStructureApiModel[]> {
    return this.httpClient.post<ProjectStructureApiModel[]>(
      `${this.apiEndPoint}/BudgetControlItem/CreateAutomatically/${projectId}`, 
      {}
    );
  }

  deleteAllStructureItems(projectId: number): Observable<void> {
    return this.httpClient.delete<void>(
      `${this.apiEndPoint}/BudgetControlItem/DeleteAllStructureItems/${projectId}`
    );
  }

  recalculateBudgetControlItemValues(budgetControlItemId: number): Observable<void> {
    return this.httpClient.post<void>(
      `${this.apiEndPoint}/BudgetControlItem/${budgetControlItemId}/Recalculate`,
      {}
    );
  }

  duplicate(budgetControlItemId: number): Observable<BudgetControlItemApiModel> {
    return this.httpClient.post<BudgetControlItemApiModel>(
      `${this.apiEndPoint}/BudgetControlItem/${budgetControlItemId}/Duplicate`,
      {}
    );
  }

  recalculateProjectStructureValues(projectStructureId: number): Observable<void> {
    return this.httpClient.post<void>(
      `${this.apiEndPoint}/BudgetControlItem/RecalculateProjectStructure/${projectStructureId}`,
      {}
    );
  }

  /**
   * Recalcula los totales de todas las partidas de control del proyecto desde sus rubros APU
   * (de las hojas hacia la raíz). Repara cantidades/costos corruptos recomputándolos del origen.
   */
  recalculateProject(projectId: number): Observable<void> {
    return this.httpClient.post<void>(
      `${this.apiEndPoint}/BudgetControlItem/RecalculateProject/${projectId}`,
      {}
    );
  }

  /**
   * Repara los acumulados de consumo real (Accumulated*) de todas las Partidas de Control del
   * proyecto. Normalmente se sincronizan solos al guardar worklogs; esto sirve cuando quedaron
   * desfasados. Idempotente.
   */
  resyncAccumulated(projectId: number): Observable<{ projectId: number; updated: number }> {
    return this.httpClient.post<{ projectId: number; updated: number }>(
      `${this.apiEndPoint}/BudgetControlItem/resync-accumulated/${projectId}`,
      {}
    );
  }

  /**
   * Árbol de Partidas de Control acumulado a una fecha de corte.
   *
   * Solo recibe el corte porque el informe es acumulado por definición: el backend suma desde el
   * inicio del proyecto hasta `cutoffDate` e ignora cualquier fecha de inicio
   * (`BudgetControlProgressService.GetDashboardTreeAsync`). El `startDate` que exige el endpoint se
   * manda en epoch para no dar la falsa impresión de que acota algo.
   * `monthOnly`: los flujos (valorizado, programado, real, utilidad) salen solo del mes del corte;
   * los saldos (por ejecutar, por completar, proyectado) siguen acumulados.
   */
  getTreeDashboard(projectId: number, cutoffDate: Date, monthOnly = false): Observable<ProgressDashboardApiModel> {
    const params = new HttpParams()
      .set('projectId', projectId.toString())
      .set('startDate', new Date(0).toISOString())
      .set('endDate', this.toLocalIso(cutoffDate))
      .set('monthOnly', String(monthOnly));
    return this.httpClient.get<ProgressDashboardApiModel>(`${this.apiEndPoint}/BudgetControlItem/dashboard-tree`, { params });
  }

  /**
   * Árbol "Todos los recursos → Tipo de Recurso → Recurso" de una Partida de Control con las
   * mismas métricas del dashboard-tree (los rubros APU agrupados se fusionan por recurso).
   */
  getResourceTree(budgetControlItemId: number, startDate: Date, endDate: Date): Observable<ProgressDashboardItemApiModel[]> {
    const params = new HttpParams()
      .set('startDate', this.toLocalIso(startDate))
      .set('endDate', this.toLocalIso(endDate));
    return this.httpClient.get<ProgressDashboardItemApiModel[]>(
      `${this.apiEndPoint}/BudgetControlItem/${budgetControlItemId}/resource-tree`, { params });
  }

  /**
   * Tareo (worklogs de personal) de una Partida de Control y sus partidas descendientes:
   * filas detalladas, entradas crudas y totales agregados, en un rango de fechas.
   */
  getWorklogs(budgetControlItemId: number, startDate: Date, endDate: Date): Observable<BciWorklogApiModel> {
    const params = new HttpParams()
      .set('startDate', this.toLocalIso(startDate))
      .set('endDate', this.toLocalIso(endDate));
    return this.httpClient.get<BciWorklogApiModel>(
      `${this.apiEndPoint}/BudgetControlItem/${budgetControlItemId}/worklogs`, { params });
  }

  /**
   * Egresos de bodega imputados a una Partida de Control y sus descendientes, con el número de
   * factura de cada movimiento. Acumulado a la fecha de corte, así que sólo lleva `endDate`.
   */
  getWarehouseMovements(budgetControlItemId: number, endDate: Date): Observable<BciWarehouseApiModel> {
    const params = new HttpParams().set('endDate', this.toLocalIso(endDate));
    return this.httpClient.get<BciWarehouseApiModel>(
      `${this.apiEndPoint}/BudgetControlItem/${budgetControlItemId}/warehouse-movements`, { params });
  }

  /**
   * Recursos consumidos en la partida y sus descendientes, con el presupuesto meta al lado: mano de
   * obra y equipo desde el tareo, materiales desde los egresos de bodega. Acumulado a la fecha de
   * corte, así que sólo lleva `endDate`.
   */
  getConsumedResources(budgetControlItemId: number, endDate: Date): Observable<BciConsumedResourcesApiModel> {
    const params = new HttpParams().set('endDate', this.toLocalIso(endDate));
    return this.httpClient.get<BciConsumedResourcesApiModel>(
      `${this.apiEndPoint}/BudgetControlItem/${budgetControlItemId}/consumed-resources`, { params });
  }

  /**
   * Catálogo plano de partidas del proyecto (sólo hojas). Alimenta el selector cuando hay que
   * corregir a qué partida se imputó un egreso de bodega.
   */
  getCatalog(projectId: number): Observable<BudgetControlItemCatalogApiModel[]> {
    return this.httpClient.get<BudgetControlItemCatalogApiModel[]>(
      `${this.apiEndPoint}/BudgetControlItem/Catalog/${projectId}`);
  }

  /**
   * Reimputa un egreso de bodega a otra partida del mismo proyecto, o lo desimputa con
   * `budgetControlItemId` en null. El cambio se escribe primero en Concreto, que es el dueño del
   * dato, y queda auditado de los dos lados.
   */
  reassignWarehouseMovement(
    concretoMovimientoId: number, request: WarehouseImputationRequest,
  ): Observable<WarehouseImputationResult> {
    return this.httpClient.put<WarehouseImputationResult>(
      `${this.apiEndPoint}/BudgetControlItem/warehouse-movements/${concretoMovimientoId}/budget-control-item`,
      request);
  }

  /**
   * Pasa todo lo imputado a una partida (tareo de personal y equipos, tareo predeterminado y egresos
   * de bodega) a otra partida hoja del mismo proyecto. La origen queda vacía.
   */
  transferWorklogs(sourceBudgetControlItemId: number, request: WorklogTransferRequest): Observable<WorklogTransferResult> {
    return this.httpClient.post<WorklogTransferResult>(
      `${this.apiEndPoint}/BudgetControlItem/${sourceBudgetControlItemId}/transfer-worklogs`, request);
  }
}
